import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';

const ChartCard = ({ loadingTime, lastLoaded, data, timeframe, currentFrames = 100, onFetchFrames }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null); // Ref for our HTML tooltip overlay
  
  const seriesRefs = useRef({ sma20: null, sma50: null, sma200: null });
  const [framesInput, setFramesInput] = useState(currentFrames);

  const [visibility, setVisibility] = useState({
    sma20: true,
    sma50: true,
    sma200: true
  });

  useEffect(() => {
    setFramesInput(currentFrames);
  }, [currentFrames]);

  useEffect(() => {
    if (seriesRefs.current.sma20) seriesRefs.current.sma20.applyOptions({ visible: visibility.sma20 });
    if (seriesRefs.current.sma50) seriesRefs.current.sma50.applyOptions({ visible: visibility.sma50 });
    if (seriesRefs.current.sma200) seriesRefs.current.sma200.applyOptions({ visible: visibility.sma200 });
  }, [visibility]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    chartContainerRef.current.innerHTML = '';

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: {
        background: { type: 'solid', color: '#000000' },
        textColor: '#7c8a9e', 
      },
      grid: {
        vertLines: { color: '#383636' }, 
        horzLines: { color: '#383636' },
      },
      crosshair: { 
        mode: 0,
        vertLine: { color: '#383636', labelBackgroundColor: '#111111' },
        horzLine: { color: '#383636', labelBackgroundColor: '#111111' }
      },
      rightPriceScale: {
        borderColor: '#111111',
        // autoScale: true, 
        scaleMargins: { top: 0.05, bottom: 0.05 },
        mode: 0, // Normal mode
      },
      timeScale: {
        borderColor: '#111111',
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: true, 
        fixRightEdge: true,
      },
      handleScale: {
        axisPressedMouseMove: { time: true },
      },
      handleScroll: { vertTouchDrag: false }
    });
    

    const sma20Series = chart.addSeries(LineSeries, {
      color: '#84AB86', 
      lineWidth: 1.5,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma20,
    });
    seriesRefs.current.sma20 = sma20Series;

    const sma50Series = chart.addSeries(LineSeries, {
      color: '#C3728E', 
      lineWidth: 1.5,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma50,
    });
    seriesRefs.current.sma50 = sma50Series;

    const sma200Series = chart.addSeries(LineSeries, {
      color: '#66B2BB', 
      lineWidth: 1.5,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma200,
    });
    seriesRefs.current.sma200 = sma200Series;

    if (Array.isArray(data) && data.length > 0) {
      const data20 = [];
      const data50 = [];
      const data200 = [];

      data.forEach(item => {
        let safeTimeStr = item.time;
        if (safeTimeStr && safeTimeStr.length === 10) safeTimeStr += "T00:00:00";
        else if (safeTimeStr) safeTimeStr = safeTimeStr.replace(' ', 'T') + ':00';
        else return; 

        const dateObj = new Date(safeTimeStr);
        let timestamp = Math.floor(dateObj.getTime() / 1000);
        timestamp = timestamp - (dateObj.getTimezoneOffset() * 60);

        if (!isNaN(timestamp)) {
          data20.push({ time: timestamp, value: item['%above20'] || 0 });
          data50.push({ time: timestamp, value: item['%above50'] || 0 });
          data200.push({ time: timestamp, value: item['%above200'] || 0 });
        }
      });

      data20.sort((a, b) => a.time - b.time);
      data50.sort((a, b) => a.time - b.time);
      data200.sort((a, b) => a.time - b.time);

      if (data20.length > 0) sma20Series.setData(data20);
      if (data50.length > 0) sma50Series.setData(data50);
      if (data200.length > 0) sma200Series.setData(data200);

      chart.timeScale().fitContent();
    }

    // 🔥 NEW: Crosshair Move Event Handling for Custom Tooltip
    chart.subscribeCrosshairMove((param) => {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;

      // Hide tooltip if pointer leaves canvas bounds or has no valid crosshair placement
      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.x > chartContainerRef.current.clientWidth ||
        param.point.y < 0 ||
        param.point.y > 400
      ) {
        tooltip.style.display = 'none';
        return;
      }

      // Convert Unix timestamp back to a readable date
      // Convert Unix timestamp back to a readable date in IST
      // 1. Reverse the timezone hack we applied when inserting the data
      const trueUnixSeconds = param.time + (new Date().getTimezoneOffset() * 60);

      // 2. Now format the TRUE timestamp into perfect IST
      const dateStr = new Date(trueUnixSeconds * 1000).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        // hour12: false
      });


      // Extract values for each visible active line series at the hovered timeline segment
      const v20 = param.seriesData.get(sma20Series);
      const v50 = param.seriesData.get(sma50Series);
      const v200 = param.seriesData.get(sma200Series);

      let tooltipHtml = `<div style="font-size: 11px; color: #94a3b8; margin-bottom: 4px; font-weight: bold;">${dateStr}</div>`;

      // Construct metrics visibility rows inside the tracker panel dynamically
      if (visibility.sma20 && v20) {
        tooltipHtml += `<div style="color: #84AB86; font-size: 12px; font-weight: 600;">% &gt; SMA 20: <span style="color: #fff">${v20.value.toFixed(2)}%</span></div>`;
      }
      if (visibility.sma50 && v50) {
        tooltipHtml += `<div style="color: #C3728E; font-size: 12px; font-weight: 600;">% &gt; SMA 50: <span style="color: #fff">${v50.value.toFixed(2)}%</span></div>`;
      }
      if (visibility.sma200 && v200) {
        tooltipHtml += `<div style="color: #66B2BB; font-size: 12px; font-weight: 600;">% &gt; SMA 200: <span style="color: #fff">${v200.value.toFixed(2)}%</span></div>`;
      }

      tooltip.innerHTML = tooltipHtml;
      tooltip.style.display = 'block';

      // Position the dynamic overlay near the cursor tracking coordinates safely
      const coordinateX = param.point.x;
      const coordinateY = param.point.y;
      
      const tooltipWidth = 150;
      const tooltipHeight = 80;
      const leftSpacing = 15;

      // Adjust coordinate mapping to prevent tooltips clipping off the right side
      let left = coordinateX + leftSpacing;
      if (left > chartContainerRef.current.clientWidth - tooltipWidth) {
        left = coordinateX - tooltipWidth - leftSpacing;
      }

      let top = coordinateY + leftSpacing;
      if (top > 400 - tooltipHeight) {
        top = coordinateY - tooltipHeight - leftSpacing;
      }

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    });

    chartRef.current = chart;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [data, timeframe, visibility]); // Rebind listener if visibility changes to hide text layers properly

  const toggleVisibility = (seriesName) => {
    setVisibility(prev => ({ ...prev, [seriesName]: !prev[seriesName] }));
  };

  const resetChartView = () => {
    if (!chartRef.current) return;

    const chart = chartRef.current;

    // 1. Reset Horizontal Zoom & Scroll (Fits all data perfectly back onto the screen)
    chart.timeScale().fitContent();

    // 2. Reset Vertical Zoom & Pan (Restores the default automatic price scale height)
    chart.priceScale('right').applyOptions({
      autoScale: true,
      priceRange: {
      minValue: 0,
      maxValue: 100,
    },
    });
  };

  return (
    <div style={{ 
      background: '#000000', 
      border: '1px solid #111111', 
      borderRadius: '10px', 
      boxShadow: '0 18px 40px #0000002e',
      overflow: 'hidden',
      padding: '15px'
    }}>
      {/* Header Block Elements */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ 
              margin: 0, color: '#e2e8f0', fontSize: '13px', fontWeight: '700', 
              letterSpacing: '0.06em', textTransform: 'uppercase' 
            }}>
              Market Breadth - {timeframe}
            </h3>
            {loadingTime && (
              <span style={{ 
                fontSize: '0.7rem', color: '#4197F7', background: 'rgba(65,151,247,0.14)', 
                padding: '2px 6px', borderRadius: '6px', border: '1px solid rgba(65,151,247,0.3)', fontWeight: 'bold'
              }}>
                ⏱️ {Number(loadingTime).toFixed(2)}s
              </span>
            )}
            {lastLoaded && (
              <span style={{ 
                fontSize: '0.7rem', color: '#94a3b8', background: 'rgba(148,163,184,0.1)', 
                padding: '2px 6px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.2)', fontWeight: '600'
              }}>
                ↻ {lastLoaded}
              </span>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600' }}>FRAMES:</span>
            <input 
              type="number" 
              value={framesInput}
              onChange={(e) => setFramesInput(parseInt(e.target.value) || '')}
              style={{ 
                width: '60px', padding: '4px 8px', borderRadius: '6px', 
                border: '1px solid #1a1a1a', background: '#0a0a0a', color: '#e2e8f0',
                fontSize: '0.85rem', outline: 'none'
              }}
            />
            <button 
              onClick={() => onFetchFrames(framesInput)}
              style={{ 
                padding: '4px 14px', 
                background: 'linear-gradient(90deg, #4196f769 -94.84%, #c872f380 162.4%)', 
                color: '#fff', border: 'none', borderRadius: '6px', 
                cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem',
                boxShadow: '#8765f61f 0 0 0 1px'
              }}
            >
              LOAD
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', fontSize: '0.85rem', fontWeight: '600' }}>
          <span 
            onClick={() => toggleVisibility('sma20')}
            style={{ color: '#84AB86', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma20 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            % &gt; SMA 20
          </span>
          <span 
            onClick={() => toggleVisibility('sma50')}
            style={{ color: '#C3728E', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma50 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            % &gt; SMA 50
          </span>
          <span 
            onClick={() => toggleVisibility('sma200')}
            style={{ color: '#66B2BB', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma200 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            % &gt; SMA 200
          </span>
        </div>
      </div>
      
      {/* Chart Canvas Wrap */}
      <div 
        style={{ position: 'relative', width: '100%', height: '400px', backgroundColor: '#000000', borderRadius: '8px', overflow: 'hidden', border: '1px solid #111111' }}
        onDoubleClick={resetChartView} // 🔥 Double-clicking anywhere on the chart canvas resets it!
      >
        <div ref={chartContainerRef} className="chart-container" style={{ width: '100%', height: '100%' }} />
        
        {/* 🔥 NEW: Floating HTML Tooltip element */}
        <div 
          ref={tooltipRef}
          style={{
            width: '160px',
            position: 'absolute',
            display: 'none',
            padding: '8px',
            boxSizing: 'border-box',
            border: '1px solid #222',
            borderRadius: '6px',
            fontFamily: 'sans-serif',
            backgroundColor: 'rgba(10, 10, 10, 0.85)',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.5)',
            textAlign: 'left',
            zIndex: 1000,
            pointerEvents: 'none', // Critical so tooltip won't block canvas mouse drags
          }}
        />

        {(!Array.isArray(data) || data.length === 0) && (
          <div style={{ 
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            backgroundColor: 'rgba(0,0,0,0.8)', color: '#64748b', zIndex: 10,
            fontSize: '0.9rem', fontWeight: '600', letterSpacing: '0.05em'
          }}>
            {typeof data === 'string' ? data : `NO DATA AVAILABLE FOR ${timeframe.toUpperCase()}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartCard;